import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faExclamationTriangle, faRedo } from '@fortawesome/free-solid-svg-icons';
import './RecordLoadProblem.css';

/**
 * "This record didn't load" — one rule and one message for detail screens.
 *
 * Ledger row 125 (Sept 2026): the trade and freight recap screens asked the
 * server for a recap, were told "not found" or "not allowed", logged it and
 * carried on. The trade recap then waited on "Loading recap…" for ever; opened
 * from the Trade Ops table, and on the freight recap always, it drew the empty
 * form instead, whose Save creates a brand-new recap.
 *
 * A screen that loads one record by id keeps what `describeLoadFailure` returns
 * and, while it is set, renders this INSTEAD of its form — never alongside it.
 *
 * The server answers "not found" both for a record that doesn't exist and for
 * one the person isn't on (it deliberately doesn't say which), so the 404
 * message names both.
 */
export const describeLoadFailure = (error) => {
    const status = error?.response?.status ?? null;
    if (status === 404) return { kind: 'not_found', status };
    if (status === 403) {
        const detail = error?.response?.data?.detail;
        return { kind: 'no_access', status, detail: typeof detail === 'string' ? detail : null };
    }
    return { kind: 'failed', status };
};

const RecordLoadProblem = ({
    problem,
    recordName,
    areaName,
    backLabel,
    onBack,
    onRetry,
    // The default text ends "Try again, or go back", which is right on a full
    // screen with a Back button and wrong inside a panel that only has Try
    // again. A host with no Back button passes its own sentence; every
    // existing caller passes nothing and reads exactly as it did before.
    failedText,
}) => {
    if (!problem) return null;

    let title;
    let text;
    if (problem.kind === 'not_found') {
        title = `This ${recordName} can't be opened`;
        text = `It doesn't exist, or it isn't shared with you. If someone sent you the link, ask them to give you access.`;
    } else if (problem.kind === 'no_access') {
        title = `You don't have access to ${areaName}`;
        text = problem.detail || 'Ask an admin to give you access.';
    } else {
        title = `This ${recordName} couldn't be loaded`;
        text = failedText || 'Something went wrong while opening it. Try again, or go back.';
    }

    return (
        <div
            className="rlp-card"
            role="alert"
            data-testid="record-load-problem"
            data-kind={problem.kind}
        >
            <FontAwesomeIcon icon={faExclamationTriangle} className="rlp-icon" />
            <div className="rlp-title">{title}</div>
            <div className="rlp-text">{text}</div>
            <div className="rlp-actions">
                {problem.kind === 'failed' && onRetry && (
                    <button type="button" className="rlp-btn rlp-btn-primary" onClick={onRetry}>
                        <FontAwesomeIcon icon={faRedo} /> Try again
                    </button>
                )}
                {onBack && (
                    <button type="button" className="rlp-btn rlp-btn-secondary" onClick={onBack}>
                        <FontAwesomeIcon icon={faChevronLeft} /> {backLabel}
                    </button>
                )}
            </div>
        </div>
    );
};

export default RecordLoadProblem;
